import { useState } from 'react';
import {
    Confirm,
    ConfirmProps,
    Create,
    customStyles,
    ListProps,
} from 'components/settings/recordList';
import {
    Section,
    SECTION_COLLECTION_NAME,
    Subsection,
    subsectionConverter,
    SUBSECTION_COLLECTION_NAME,
} from 'models';
import './sectionList.css';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { collection, doc, addDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, useAuthState } from 'firebaseStore';
import { CollectionState, useCollectionContext } from 'components/settings/collectionContext';
import Modal from 'react-modal';
import { useCommit } from 'commits';

Modal.setAppElement('#modal');

interface SubsectionProps {
    subsectionIndexId: string;
    onDeleteItem: (_: Subsection, message: string) => void;
    onEditItem: (_: Subsection) => void;
}
const SectionSubsection = ({ subsectionIndexId, onDeleteItem, onEditItem }: SubsectionProps) => {
    const [, pending, , subsectionMap] = useCollectionContext(
        SUBSECTION_COLLECTION_NAME
    ) as CollectionState<Subsection>;
    const [confirmProps, setConfirmProps] = useState<ConfirmProps>();
    if (pending) {
        return null;
    }
    const subsection = subsectionMap.get(subsectionIndexId);
    if (!subsection) {
        console.error(`Subsection ID ${subsectionIndexId} has no corresponding record`);
        return null;
    }
    const name = subsection.name || subsectionIndexId;
    return (
        <div key={subsectionIndexId} className="subsection">
            <Modal
                isOpen={!!confirmProps}
                style={customStyles(2)}
                onRequestClose={() => setConfirmProps(undefined)}
            >
                {confirmProps ? <Confirm {...confirmProps} /> : null}
            </Modal>
            <div>- {name}</div>
            <div style={{ flexGrow: 1 }}>{subsection.content || ''}</div>
            <div className="subsectionEdit">
                <EditIcon
                    style={{ cursor: 'pointer', zIndex: 10 }}
                    onClick={() => onEditItem(subsection)}
                />
                <DeleteOutlineIcon
                    style={{ cursor: 'pointer', zIndex: 10 }}
                    onClick={() => {
                        return setConfirmProps({
                            title: `You are about to delete subsection ${name}`,
                            text: `This subsection will be deleted, and contracts that contain it will be updated. Are you sure you want to continue?`,
                            onClick: async (message) => {
                                onDeleteItem(subsection, message as string);
                                setConfirmProps(undefined);
                            },
                            setModalIsOpen: () => setConfirmProps(undefined),
                            actionLabel: 'continue',
                            withMessage: true,
                        });
                    }}
                />
            </div>
        </div>
    );
};

interface SectionItemProps {
    record: Section;
    checked: boolean;
    onChange: (_: Section) => void;
    onEditItem: (_: Section) => void;
    onDeleteItem: (_: Section) => void;
}
export const SectionItem = ({
    record,
    checked,
    onChange,
    onEditItem,
    onDeleteItem,
}: SectionItemProps) => {
    const [expanded, setExpanded] = useState(true);
    const [value, setValue] = useState('');
    const [, , tenant] = useAuthState();
    const commit = useCommit<Subsection>();
    const [modalIsOpen, setModalIsOpen] = useState<Subsection>();
    const collectionRef = collection(db, SUBSECTION_COLLECTION_NAME).withConverter(
        subsectionConverter
    );
    const applySubsectionUpdate = () => {
        const { subsectionIndexIds } = record;
        const data = { subsectionIndexIds };
        const docRef = doc(db, SECTION_COLLECTION_NAME, record.id);
        setDoc(docRef, data, { merge: true, mergeFields: ['subsectionIndexIds'] }).then(() =>
            setValue('')
        );
    };
    const data = { name: value, tenantId: tenant, sectionIndexId: record.indexId() };
    const onSave = () => {
        addDoc(collectionRef, data).then(
            async (newDocRef) => {
                const newSubsection = new Subsection({ ...data, id: newDocRef.id });
                await commit({ current: newSubsection, action: 'add', message: '' });
                return updateDoc(newDocRef, newSubsection.toFirestore()).then(
                    () => {
                        record.subsectionIndexIds.push(newSubsection.indexId());
                        return applySubsectionUpdate();
                    },
                    (error) => {
                        console.error(
                            'SectionItem updateDoc error',
                            SUBSECTION_COLLECTION_NAME,
                            error
                        );
                    }
                );
            },
            (error) => {
                console.error('SectionItem addDoc error', SUBSECTION_COLLECTION_NAME, error);
            }
        );
    };

    const onDeleteSubsection = async (subsectionToDelete: Subsection, message: string) => {
        await commit({ current: subsectionToDelete, action: 'delete', message });
        deleteDoc(doc(collectionRef, subsectionToDelete.id)).then(
            () => {
                record.subsectionIndexIds = record.subsectionIndexIds.filter(
                    (subsectionIndexId) => subsectionIndexId !== subsectionToDelete.indexId()
                );
                return applySubsectionUpdate();
            },
            (error) => console.error('onDeleteSubsection error', error)
        );
    };

    const onEditSubsection = async (subsectionToEdit: Subsection) => {
        setModalIsOpen(subsectionToEdit);
    };

    return (
        <div key={record.id} className="sectionItemRecord" style={{ margin: '13px 22px 13px 0' }}>
            <Modal
                isOpen={!!modalIsOpen}
                style={customStyles(2)}
                onRequestClose={() => setModalIsOpen(undefined)}
            >
                {modalIsOpen && (
                    <Create
                        initialState={modalIsOpen}
                        label="subsection"
                        setModalIsOpen={setModalIsOpen}
                        onSave={async (updated, message) => {
                            const current = new Subsection({ ...modalIsOpen, ...updated });
                            await commit({
                                previous: modalIsOpen,
                                current,
                                action: 'update',
                                message,
                            });
                            updateDoc(
                                doc(db, SUBSECTION_COLLECTION_NAME, updated.id),
                                current.toFirestore()
                            ).then(() => setModalIsOpen(undefined));
                        }}
                        columns={[
                            {
                                field: 'name',
                                label: 'Subsection',
                                input: true,
                                required: true,
                            },
                        ]}
                        name="subsection"
                    />
                )}
            </Modal>
            <input
                type="checkbox"
                className="itemSelect"
                checked={checked}
                style={{ zIndex: 10, cursor: 'pointer' }}
                onChange={() => onChange(record)}
            />

            <div style={{ flexGrow: 1, paddingLeft: '21px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                    }}
                    onClick={() => setExpanded(!expanded)}
                >
                    <div className="itemRecordSection">
                        <div className="item" key={`${record.id}-name-${record.name}`}>
                            {record.name}
                        </div>
                    </div>
                    <div className="itemRecordSection">
                        <div className="itemRecordEdit">
                            <EditIcon
                                style={{ cursor: 'pointer', zIndex: 10 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEditItem(record);
                                }}
                            />
                            <DeleteOutlineIcon
                                style={{ cursor: 'pointer', zIndex: 10 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteItem(record);
                                }}
                            />
                        </div>

                        {record.coverage ? (
                            <div className="item" key={`${record.id}-coverage-${record.coverage}`}>
                                Coverage section
                            </div>
                        ) : null}

                        <div style={{ marginLeft: '22px', marginTop: '6px' }}>
                            {expanded ? (
                                <ArrowLeftIcon htmlColor="#818181" />
                            ) : (
                                <ArrowDropDownIcon htmlColor="#818181" />
                            )}
                        </div>
                    </div>
                </div>
                {expanded ? (
                    <div style={{ marginLeft: '20px', marginTop: '0px' }}>
                        <div>
                            {record.subsectionIndexIds.map((subsectionIndexId) => (
                                <SectionSubsection
                                    key={subsectionIndexId}
                                    subsectionIndexId={subsectionIndexId}
                                    onDeleteItem={(subsection, message) =>
                                        onDeleteSubsection(subsection, message)
                                    }
                                    onEditItem={(subsection) => onEditSubsection(subsection)}
                                />
                            ))}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginLeft: '8px',
                            }}
                        >
                            <input
                                value={value}
                                style={{ width: '452px' }}
                                onChange={(e) => setValue(e.target.value)}
                            />
                            <div
                                style={{
                                    marginRight: '2px',
                                    cursor: value ? 'pointer' : 'inherit',
                                }}
                                onClick={value ? onSave : () => null}
                            >
                                <AddIcon fontSize="small" htmlColor={value ? 'black' : '#818181'} />
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export interface SectionListProps extends ListProps<Section> {
    records: Section[];
    onEditItem?: (_: Section) => void;
    onDeleteItem?: (_: Section) => void;
}
export const SectionList = ({
    records,
    selectedRecords,
    setSelectedRecords,
    onEditItem,
    onDeleteItem,
}: SectionListProps) => {
    if (!onEditItem || !onDeleteItem) {
        throw new Error('section list missing props');
    }
    return (
        <div className="listRecords">
            {records?.map((record) => {
                return (
                    <SectionItem
                        onEditItem={onEditItem}
                        onDeleteItem={onDeleteItem}
                        checked={!!selectedRecords.find((i) => i === record.id)}
                        record={record}
                        key={record.id}
                        onChange={() =>
                            selectedRecords.find((i) => i === record.id)
                                ? setSelectedRecords(
                                      selectedRecords.filter((i) => i !== record.id),
                                      record
                                  )
                                : setSelectedRecords(selectedRecords.concat([record.id]), record)
                        }
                    />
                );
            })}
        </div>
    );
};

export const renderSectionList =
    ({ records, ...props }: ListProps<Section>) =>
    () =>
        <SectionList records={records || []} {...props} />;
